/*
 * Sheets, dialogs, toasts and the main-button fallback — telekit's sheet.js, dialog.js
 * and toast.js (MIT, © 2026 Lulzx) as React components. A sheet claims Telegram's
 * BackButton while open, traps focus, and closes on Escape or scrim tap. At ≥768px it
 * becomes a centered modal, the way Telegram Desktop shows popups.
 */
import clsx from "clsx";
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { haptic, onDialog, useMainButtonFallback, useTelegramBack, type DialogRequest } from "../../lib/tg";
import { I } from "./icons";
import { Chip } from "./index";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useFocusTrap(ref: React.RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active || !ref.current) return;
    const el = ref.current;
    const prev = document.activeElement as HTMLElement | null;
    const first = el.querySelector<HTMLElement>("[autofocus]") ?? el.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? el).focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const nodes = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => n.offsetParent !== null);
      if (!nodes.length) return;
      const a = nodes[0]!;
      const z = nodes[nodes.length - 1]!;
      if (e.shiftKey && document.activeElement === a) {
        e.preventDefault();
        z.focus();
      } else if (!e.shiftKey && document.activeElement === z) {
        e.preventDefault();
        a.focus();
      }
    };
    el.addEventListener("keydown", onKey);
    return () => {
      el.removeEventListener("keydown", onKey);
      prev?.focus?.({ preventScroll: true });
    };
  }, [active, ref]);
}

export function Sheet({
  open,
  onClose,
  title,
  lede,
  children,
  footer,
  wide,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  lede?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  labelledBy?: string;
}) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
    } else if (mounted) {
      setClosing(true);
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const t = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, reduce ? 0 : 240);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useTelegramBack(open ? onClose : null);
  useFocusTrap(ref, open && mounted);
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", key);
      html.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!mounted) return null;
  return createPortal(
    <>
      <div className={clsx("tk-scrim", closing && "tk-closing")} onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? (title ? id : undefined)}
        tabIndex={-1}
        className={clsx("tk-sheet", closing && "tk-closing", wide && "tk-wide-sheet")}
      >
        <div className="tk-grab" aria-hidden="true" />
        <button type="button" className="tk-sheet-close" onClick={onClose} aria-label="Close">
          <I.close />
        </button>
        {title && <h2 id={id}>{title}</h2>}
        {lede && <p className="tk-lede">{lede}</p>}
        <div className="tk-sheet-body px-4">{children}</div>
        {footer && (
          <div className="tk-sheet-footer">
            <div className="tk-sheet-footer-inner">{footer}</div>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

/** Renders drawn dialogs requested through lib/tg (confirmDialog/alertDialog) when Telegram can't. */
export function DialogHost() {
  const [req, setReq] = useState<DialogRequest | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => onDialog(setReq), []);
  useFocusTrap(ref, !!req);
  useEffect(() => {
    if (!req) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") req.resolve(req.buttons.find((b) => b.type === "cancel")?.id ?? null);
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [req]);
  if (!req) return null;
  const stacked = req.buttons.length > 2;
  return createPortal(
    <div className="tk-dialog-scrim">
      <div ref={ref} className="tk-dialog" role="alertdialog" aria-modal="true" aria-label={req.title ?? "Confirm"} aria-describedby="tk-dialog-msg">
        {req.title && <h2>{req.title}</h2>}
        <p id="tk-dialog-msg">{req.message}</p>
        <div className={clsx("tk-dialog-actions", stacked && "tk-stacked")}>
          {req.buttons.map((b) => (
            <button
              key={b.id}
              type="button"
              autoFocus={b.type === "cancel"}
              className={clsx("tk-dialog-btn", b.type === "destructive" && "tk-destructive", b.type === "default" && "tk-default")}
              onClick={() => req.resolve(b.id)}
            >
              {b.text}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ---------------- toast ---------------- */

type Toast = { id: number; text: ReactNode; action?: { label: string; run: () => void }; tone?: "error" | "success" };
const ToastCtx = createContext<(t: Omit<Toast, "id">) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = useCallback((t: Omit<Toast, "id">) => {
    if (t.tone === "error") haptic.notify("error");
    else if (t.tone === "success") haptic.notify("success");
    setToast({ ...t, id: Date.now() });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), t.action ? 6000 : 3200);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {createPortal(
        <div aria-live="polite" aria-atomic="true">
          {toast && (
            <div key={toast.id} className="tk-toast" role="status">
              {toast.tone === "error" && <I.alert width={18} height={18} style={{ color: "#ff8a80", flex: "none" }} />}
              {toast.tone === "success" && <I.check width={18} height={18} style={{ color: "#7ddc8a", flex: "none" }} />}
              <span className="tk-toast-text">{toast.text}</span>
              {toast.action && (
                <button
                  type="button"
                  onClick={() => {
                    toast.action!.run();
                    setToast(null);
                  }}
                >
                  {toast.action.label}
                </button>
              )}
            </div>
          )}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  );
}

/** Browser stand-in for Telegram's MainButton: a bottom bar on phones, a floating button on wide layouts. */
export function FallbackMainButton() {
  const spec = useMainButtonFallback();
  if (!spec) return null;
  return (
    <div className="tk-mainbutton-bar">
      <Chip tone={spec.destructive ? "danger-solid" : "primary"} lg loading={spec.progress} disabled={spec.enabled === false} onClick={spec.onClick}>
        {spec.text}
      </Chip>
    </div>
  );
}
